// src/app/data/cheap-life-sites.ts

export interface CheapLifeSite {
  id: string;
  name: string;
  url: string;
  description: string;
  category: string;
  icon: string;
  features: string[];
  rating?: number;
  affiliate?: boolean;
  freeShipping?: boolean;
}

export const cheapLifeSites: CheapLifeSite[] = [
  // Electronics & Gadgets
  {
    id: "aliexpress",
    name: "AliExpress",
    url: "https://aliexpress.com",
    description: "Chinese marketplace with incredibly cheap electronics, gadgets, and consumer goods",
    category: "electronics",
    icon: "Globe",
    features: [
      "Extremely low prices",
      "Bulk buying options",
      "Direct from manufacturers",
      "International shipping deals"
    ],
    rating: 4.1,
    freeShipping: true
  },
  {
    id: "newegg",
    name: "Newegg",
    url: "https://newegg.com",
    description: "Computer hardware and electronics retailer with frequent sales and combo deals",
    category: "electronics",
    icon: "Monitor",
    features: [
      "Computer parts and accessories",
      "Daily deals and shell shockers",
      "Combo deals and bundles",
      "Price matching guarantee"
    ],
    rating: 4.4,
    freeShipping: true
  },

  // Clothing & Fashion
  {
    id: "shein",
    name: "SHEIN",
    url: "https://shein.com",
    description: "Fast fashion retailer with trendy clothing at incredibly low prices",
    category: "clothing",
    icon: "Shirt",
    features: [
      "Trendy fashion at low prices",
      "Daily new arrivals",
      "Flash sales and promotions",
      "International shipping"
    ],
    rating: 4.0,
    freeShipping: true
  },
  {
    id: "asos",
    name: "ASOS",
    url: "https://asos.com",
    description: "Online fashion retailer with sales, outlet section, and marketplace items",
    category: "clothing",
    icon: "ShoppingBag",
    features: [
      "Wide range of fashion brands",
      "ASOS Outlet for discounted items",
      "Marketplace with unique finds",
      "Frequent sales and promotions"
    ],
    rating: 4.1
  },
  {
    id: "hm",
    name: "H&M",
    url: "https://hm.com",
    description: "Fast fashion retailer with regular sales and outlet stores",
    category: "clothing",
    icon: "User",
    features: [
      "Conscious collection deals",
      "Online exclusive discounts",
      "Outlet section with up to 50% off",
      "Student and member discounts"
    ],
    rating: 4.0
  },
  {
    id: "uniqlo",
    name: "Uniqlo",
    url: "https://uniqlo.com",
    description: "Quality basics and clothing with frequent sales and promotions",
    category: "clothing",
    icon: "Circle",
    features: [
      "High-quality basics",
      "HeatTech and other innovative fabrics",
      "Regular sales events",
      "Online exclusive deals"
    ],
    rating: 4.3
  },
  {
    id: "forever21",
    name: "Forever 21",
    url: "https://forever21.com",
    description: "Trendy clothing for young adults with frequent sales and outlet pricing",
    category: "clothing",
    icon: "Sparkles",
    features: [
      "Trendy affordable fashion",
      "Regular 21-dollar sales",
      "Online flash sales",
      "Student discounts"
    ],
    rating: 3.9
  },

  // Home & Furniture
  {
    id: "wayfair",
    name: "Wayfair",
    url: "https://wayfair.com",
    description: "Home goods and furniture retailer with daily sales and flash deals",
    category: "home",
    icon: "Home",
    features: [
      "Wide selection of furniture",
      "Daily sales and flash deals",
      "Wayfair Professional discounts",
      "Free shipping on many items"
    ],
    rating: 4.2,
    freeShipping: true
  },
  {
    id: "ikea",
    name: "IKEA",
    url: "https://ikea.com",
    description: "Swedish furniture retailer with affordable, functional home solutions",
    category: "home",
    icon: "Package",
    features: [
      "Affordable furniture solutions",
      "Flat-pack assembly",
      "Online planning tools",
      "AS-IS and outlet sections"
    ],
    rating: 4.1
  },
  {
    id: "dollar-tree",
    name: "Dollar Tree",
    url: "https://dollartree.com",
    description: "Everything for $1.25 or less - home goods, party supplies, and more",
    category: "home",
    icon: "DollarSign",
    features: [
      "Everything $1.25 or less",
      "Seasonal and holiday items",
      "Bulk party supplies",
      "Household essentials"
    ],
    rating: 4.0
  },
  {
    id: "five-below",
    name: "Five Below",
    url: "https://fivebelow.com",
    description: "Trendy merchandise for $5 and under - home decor, gadgets, and more",
    category: "home",
    icon: "Minus",
    features: [
      "Everything $5 and under",
      "Trendy home decor",
      "Party supplies and gifts",
      "Seasonal merchandise"
    ],
    rating: 4.1
  },

  // Books & Media
  {
    id: "thrift-books",
    name: "ThriftBooks",
    url: "https://thriftbooks.com",
    description: "Used books marketplace with millions of titles at discounted prices",
    category: "books",
    icon: "Book",
    features: [
      "Millions of used books",
      "Free shipping on orders over $10",
      "New books at discount prices",
      "Textbook rentals and sales"
    ],
    rating: 4.4,
    freeShipping: true
  },
  {
    id: "book-depository",
    name: "Book Depository",
    url: "https://bookdepository.com",
    description: "Online bookstore with free worldwide shipping and competitive prices",
    category: "books",
    icon: "BookOpen",
    features: [
      "Free worldwide shipping",
      "New and used books",
      "Competitive pricing",
      "No minimum order requirements"
    ],
    rating: 4.5,
    freeShipping: true
  },
  {
    id: "better-world-books",
    name: "Better World Books",
    url: "https://betterworldbooks.com",
    description: "Online bookstore that supports literacy programs with affordable books",
    category: "books",
    icon: "Heart",
    features: [
      "Supports literacy programs",
      "Used and new books",
      "Free shipping options",
      "Textbook marketplace"
    ],
    rating: 4.3,
    freeShipping: true
  },

  // Sports & Outdoors
  {
    id: "rei-outlet",
    name: "REI Outlet",
    url: "https://rei.com/outlet",
    description: "Outdoor gear and clothing from REI at discounted prices",
    category: "sports",
    icon: "Mountain",
    features: [
      "Outdoor gear and clothing",
      "Closeout and overstock items",
      "Used gear marketplace",
      "Expert advice and gear guides"
    ],
    rating: 4.4
  },
  {
    id: "dick-s-sporting-goods",
    name: "Dick's Sporting Goods",
    url: "https://dickssportinggoods.com",
    description: "Sports equipment and apparel with regular sales and clearance sections",
    category: "sports",
    icon: "Trophy",
    features: [
      "Wide range of sports equipment",
      "Regular sales and clearance",
      "Team sports gear",
      "Fitness equipment and apparel"
    ],
    rating: 4.1
  },
  {
    id: "academy-sports",
    name: "Academy Sports",
    url: "https://academy.com",
    description: "Sports and outdoors retailer with hunting, fishing, and camping gear",
    category: "sports",
    icon: "Target",
    features: [
      "Hunting and fishing gear",
      "Camping and outdoor equipment",
      "Sports team uniforms",
      "Weekly sales and promotions"
    ],
    rating: 4.2
  },

  // Beauty & Personal Care
  {
    id: "ulta-beauty",
    name: "Ulta Beauty",
    url: "https://ulta.com",
    description: "Beauty products with regular sales, flash deals, and rewards program",
    category: "beauty",
    icon: "Sparkles",
    features: [
      "Wide range of beauty products",
      "Flash sales and deals",
      "Ultamate Rewards program",
      "Free samples with purchases"
    ],
    rating: 4.3
  },
  {
    id: "sephora",
    name: "Sephora",
    url: "https://sephora.com",
    description: "Beauty and skincare retailer with sales, samples, and beauty insider rewards",
    category: "beauty",
    icon: "Palette",
    features: [
      "Luxury and drugstore brands",
      "Beauty Insider rewards program",
      "Free samples and gifts",
      "Flash sales and promotions"
    ],
    rating: 4.2
  },
  {
    id: "bath-body-works",
    name: "Bath & Body Works",
    url: "https://bathandbodyworks.com",
    description: "Body care and home fragrance products with frequent sales and outlet",
    category: "beauty",
    icon: "Droplets",
    features: [
      "Body care and candles",
      "Frequent sales and promotions",
      "Semi-annual sale events",
      "Outlet section with discounts"
    ],
    rating: 4.0
  },

  // Food & Groceries
  {
    id: "costco",
    name: "Costco",
    url: "https://costco.com",
    description: "Warehouse club with bulk items, groceries, and electronics at wholesale prices",
    category: "food",
    icon: "Package",
    features: [
      "Bulk grocery items",
      "Kirkland Signature brand",
      "Membership required but worth it",
      "Wide variety of products"
    ],
    rating: 4.5
  },
  {
    id: "aldi",
    name: "ALDI",
    url: "https://aldi.us",
    description: "Discount supermarket chain with high-quality products at low prices",
    category: "food",
    icon: "ShoppingCart",
    features: [
      "High-quality private label brands",
      "Weekly specials and deals",
      "Limited selection keeps prices low",
      "Eco-friendly practices"
    ],
    rating: 4.2
  },
  {
    id: "trader-joes",
    name: "Trader Joe's",
    url: "https://traderjoes.com",
    description: "Gourmet grocery store with unique products at affordable prices",
    category: "food",
    icon: "Leaf",
    features: [
      "Unique and gourmet items",
      "Private label quality brands",
      "Weekly specials",
      "Focus on organic and natural foods"
    ],
    rating: 4.4
  },

  // Automotive
  {
    id: "rockauto",
    name: "RockAuto",
    url: "https://rockauto.com",
    description: "Online auto parts retailer with competitive pricing and fast shipping",
    category: "auto",
    icon: "Car",
    features: [
      "Wide selection of auto parts",
      "Competitive pricing",
      "Fast shipping",
      "Detailed product information"
    ],
    rating: 4.6
  },
  {
    id: "advance-auto-parts",
    name: "Advance Auto Parts",
    url: "https://advanceautoparts.com",
    description: "Auto parts retailer with online ordering and in-store pickup",
    category: "auto",
    icon: "Wrench",
    features: [
      "Extensive parts catalog",
      "Same day pickup options",
      "Professional advice",
      "Regular sales and promotions"
    ],
    rating: 4.1
  },

  // Tools & Hardware
  {
    id: "harbor-freight",
    name: "Harbor Freight",
    url: "https://harborfreight.com",
    description: "Tools and hardware store with incredible deals and frequent sales",
    category: "tools",
    icon: "Hammer",
    features: [
      "Tools and hardware at low prices",
      "Frequent coupon deals",
      "Quality tools at discount prices",
      "Large selection of power tools"
    ],
    rating: 4.3
  },

  // Specialty & Other
  {
    id: "dollar-general",
    name: "Dollar General",
    url: "https://dollargeneral.com",
    description: "Convenience store chain with everyday items for $1 or less",
    category: "general",
    icon: "Store",
    features: [
      "Everyday essentials",
      "Household items",
      "Snacks and beverages",
      "Seasonal merchandise"
    ],
    rating: 3.9
  },
  {
    id: "big-lots",
    name: "Big Lots",
    url: "https://biglots.com",
    description: "Closeout retailer with brand name merchandise at discounted prices",
    category: "general",
    icon: "Package",
    features: [
      "Brand name closeout merchandise",
      "Home goods and furniture",
      "Electronics and appliances",
      "Seasonal and holiday items"
    ],
    rating: 4.0
  },
  {
    id: "tj-maxx",
    name: "TJ Maxx",
    url: "https://tjmaxx.com",
    description: "Off-price retailer with designer and brand name merchandise at discounted prices",
    category: "general",
    icon: "Star",
    features: [
      "Designer brands at discount prices",
      "Home goods and clothing",
      "Seasonal merchandise",
      "New arrivals weekly"
    ],
    rating: 4.1
  },
  {
    id: "ross-dress-for-less",
    name: "Ross Dress for Less",
    url: "https://rossstores.com",
    description: "Off-price retailer specializing in apparel, shoes, and home fashions",
    category: "general",
    icon: "Shirt",
    features: [
      "Apparel and accessories",
      "Home decor and furnishings",
      "Brand name merchandise",
      "Weekly new arrivals"
    ],
    rating: 4.0
  },
  {
    id: "groupon",
    name: "Groupon",
    url: "https://groupon.com",
    description: "Deal-of-the-day website with discounted products and services",
    category: "deals",
    icon: "Tag",
    features: [
      "Daily deals and coupons",
      "Local services and experiences",
      "Product discounts",
      "Flash sales and limited-time offers"
    ],
    rating: 4.1
  },
  {
    id: "retailmenot",
    name: "RetailMeNot",
    url: "https://retailmenot.com",
    description: "Coupon and discount code website for thousands of online retailers",
    category: "deals",
    icon: "Percent",
    features: [
      "Thousands of store coupons",
      "Cash back offers",
      "Weekly ads and flyers",
      "Price comparison tools"
    ],
    rating: 4.2
  },

  // Games & Steam Keys
  {
    id: "steam",
    name: "Steam",
    url: "https://store.steampowered.com",
    description: "Official Steam store with regular sales, bundles, and seasonal discounts on PC games",
    category: "games",
    icon: "Gamepad2",
    features: [
      "Daily and weekly sales",
      "Seasonal sales (Summer, Winter, Halloween)",
      "Game bundles and packs",
      "Steam wallet and gift cards"
    ],
    rating: 4.6
  },
  {
    id: "humble-bundle",
    name: "Humble Bundle",
    url: "https://www.humblebundle.com",
    description: "Weekly game bundles with charity options and DRM-free game downloads",
    category: "games",
    icon: "Package",
    features: [
      "Pay what you want bundles",
      "Charity donation options",
      "DRM-free downloads",
      "Monthly subscription service"
    ],
    rating: 4.5
  },
  {
    id: "gog",
    name: "GOG (Good Old Games)",
    url: "https://www.gog.com",
    description: "DRM-free digital game store with classic and modern games at competitive prices",
    category: "games",
    icon: "Gamepad2",
    features: [
      "DRM-free downloads",
      "Regular sales and bundles",
      "Classic games collection",
      "Galaxy client for game management"
    ],
    rating: 4.4
  },
  {
    id: "gamers-gate",
    name: "GamersGate",
    url: "https://www.gamersgate.com",
    description: "Digital game store with competitive pricing and frequent sales",
    category: "games",
    icon: "ShoppingCart",
    features: [
      "PC games at discount prices",
      "Regional pricing",
      "Fast digital delivery",
      "CD key delivery"
    ],
    rating: 4.1
  },
  {
    id: "green-man-gaming",
    name: "Green Man Gaming",
    url: "https://www.greenmangaming.com",
    description: "Independent digital game retailer with competitive prices and charity support",
    category: "games",
    icon: "Leaf",
    features: [
      "Charity donation with every purchase",
      "Flash sales and daily deals",
      "Bundle deals",
      "Pre-order bonuses"
    ],
    rating: 4.3
  },
  {
    id: "itch-io",
    name: "itch.io",
    url: "https://itch.io",
    description: "Indie game marketplace supporting developers with pay-what-you-want pricing",
    category: "games",
    icon: "Zap",
    features: [
      "Pay what you want",
      "Support indie developers",
      "Wide variety of indie games",
      "DRM-free downloads"
    ],
    rating: 4.4
  },
  {
    id: "fanatical",
    name: "Fanatical",
    url: "https://www.fanatical.com",
    description: "Digital game store with flash sales, bundles, and competitive pricing",
    category: "games",
    icon: "Star",
    features: [
      "Flash sales and lightning deals",
      "Game bundles",
      "Pre-order deals",
      "Digital game keys"
    ],
    rating: 4.2
  },
  {
    id: "games-planet",
    name: "GamesPlanet",
    url: "https://us.gamesplanet.com",
    description: "International digital game store with regional pricing and frequent sales",
    category: "games",
    icon: "Globe",
    features: [
      "Regional pricing",
      "International game access",
      "Regular sales and promotions",
      "Digital delivery"
    ],
    rating: 4.0
  },
  {
    id: "voidu",
    name: "Voidu",
    url: "https://www.voidu.com",
    description: "Digital game marketplace with competitive pricing and instant delivery",
    category: "games",
    icon: "Key",
    features: [
      "Competitive game prices",
      "Instant digital delivery",
      "Wide game selection",
      "CD key activation"
    ],
    rating: 4.1
  },
  {
    id: "hrk-game",
    name: "HRK Game",
    url: "https://www.hrkgame.com",
    description: "Digital game store specializing in Steam keys and competitive pricing",
    category: "games",
    icon: "Key",
    features: [
      "Steam game keys",
      "Competitive pricing",
      "Fast delivery",
      "Secure transactions"
    ],
    rating: 4.0
  },
  {
    id: "gamivo",
    name: "Gamivo",
    url: "https://www.gamivo.com",
    description: "Digital game marketplace with cheap Steam keys and game codes",
    category: "games",
    icon: "Key",
    features: [
      "Steam keys and game codes",
      "Competitive pricing",
      "Instant delivery",
      "Customer support"
    ],
    rating: 4.1
  },
  {
    id: "eneba",
    name: "Eneba",
    url: "https://www.eneba.com",
    description: "Global digital marketplace for game keys with competitive pricing",
    category: "games",
    icon: "Key",
    features: [
      "Global marketplace",
      "Steam and other platform keys",
      "Competitive pricing",
      "Instant delivery"
    ],
    rating: 4.2
  },
  {
    id: "kinguin",
    name: "Kinguin",
    url: "https://www.kinguin.net",
    description: "Digital game marketplace with cheap game keys and instant delivery",
    category: "games",
    icon: "Key",
    features: [
      "Steam game keys",
      "Instant digital delivery",
      "Competitive pricing",
      "Wide game selection"
    ],
    rating: 4.0
  },
  {
    id: "gamebillet",
    name: "GameBillet",
    url: "https://www.gamebillet.com",
    description: "Digital game store offering Steam keys at competitive prices",
    category: "games",
    icon: "Key",
    features: [
      "Steam game keys",
      "Competitive pricing",
      "Fast delivery",
      "Regular sales"
    ],
    rating: 4.1
  },
  {
    id: "cdkeys",
    name: "CDKeys",
    url: "https://www.cdkeys.com",
    description: "Digital game store specializing in cheap game keys and activation codes",
    category: "games",
    icon: "Key",
    features: [
      "Steam and platform keys",
      "Cheap game codes",
      "Instant delivery",
      "Global shipping"
    ],
    rating: 4.2
  },
  {
    id: "dlcompare",
    name: "DLCompare",
    url: "https://www.dlcompare.com",
    description: "Game price comparison site helping you find the cheapest game keys",
    category: "games",
    icon: "Search",
    features: [
      "Price comparison across stores",
      "Find cheapest game keys",
      "Multiple retailers",
      "Real-time pricing"
    ],
    rating: 4.3
  },
  {
    id: "g2a",
    name: "G2A",
    url: "https://www.g2a.com",
    description: "Global marketplace for game keys with competitive prices across all platforms including Steam, Origin, Xbox Live, GOG, Ubisoft Connect, and PSN",
    category: "games",
    icon: "Gamepad2",
    features: [
      "Huge selection across all gaming platforms",
      "Instant digital delivery",
      "Regular sales and discounts up to 90%",
      "G2A Plus subscription for additional perks",
      "Buyer protection program"
    ],
    rating: 4.1
  },
  {
    id: "gg-deals",
    name: "GG.deals",
    url: "https://gg.deals",
    description: "Price comparison platform tracking offers from all trusted PC keyshops, helping you find the absolute best deals in one place",
    category: "games",
    icon: "Search",
    features: [
      "Advanced search filters by genre and features",
      "Price history tracking and trend analysis",
      "Custom price alerts for wishlist games",
      "Tracks deals from 50+ legitimate stores",
      "Bundle deals aggregation from multiple sites"
    ],
    rating: 4.6
  },
  {
    id: "indiegala",
    name: "IndieGala",
    url: "https://www.indiegala.com",
    description: "Digital distribution platform serving 3+ million gamers with focus on PC games and Steam keys at discounted prices",
    category: "games",
    icon: "Sparkles",
    features: [
      "New deals every hour with up to 98% off",
      "Focus on indie and lesser-known titles",
      "Thousands of PC Steam keys available",
      "Bundle deals and single game purchases",
      "Free games giveaways regularly"
    ],
    rating: 4.1
  },
  {
    id: "instant-gaming",
    name: "Instant Gaming",
    url: "https://www.instant-gaming.com",
    description: "Hard discount website by gamers for gamers, offering instant 24/7 delivery with 700,000+ positive Trustpilot reviews",
    category: "games",
    icon: "Zap",
    features: [
      "Instant digital delivery within seconds",
      "No third-party sellers - direct from suppliers",
      "Support for PC, PlayStation, Xbox, and Switch",
      "Gift cards for Steam, Netflix, Amazon, Spotify",
      "10+ years of trusted service"
    ],
    rating: 4.6
  },
  {
    id: "allkeyshop",
    name: "AllKeyShop",
    url: "https://www.allkeyshop.com",
    description: "Leading price comparison platform in USA, UK, and EU for CD keys and game codes with 190,552+ games tracked",
    category: "games",
    icon: "BarChart3",
    features: [
      "Compare prices from 59+ trusted sellers",
      "Steam, Xbox Game Pass, and PS Plus price tracker",
      "Discount codes for additional savings",
      "In-depth editorial content and buying guides",
      "Coverage of PC, PlayStation, Xbox, and Switch"
    ],
    rating: 4.4
  },
  {
    id: "loaded",
    name: "Loaded",
    url: "https://www.loaded.com/pc",
    description: "PC game retailer offering instant digital delivery of authentic CD keys at low prices with multi-platform support",
    category: "games",
    icon: "Download",
    features: [
      "Instant email delivery within minutes",
      "Compatible with Steam, Epic, EA App, Battle.net",
      "Bulk purchasing enables lower prices",
      "Clear region-lock information provided",
      "Long-standing relationships with distributors"
    ],
    rating: 4.0
  },
  {
    id: "epic-games-store",
    name: "Epic Games Store",
    url: "https://www.epicgames.com/store",
    description: "Digital game store offering free games every week plus exclusive titles and regular sales",
    category: "games",
    icon: "Gift",
    features: [
      "Free games every week - keep them forever",
      "Exclusive game releases and deals",
      "Epic Games launcher with cloud saves",
      "Regular mega sales with deep discounts",
      "Creator support program"
    ],
    rating: 4.3
  }
];

export const categories = [
  { id: "electronics", name: "Electronics & Gadgets", icon: "Monitor" },
  { id: "clothing", name: "Clothing & Fashion", icon: "Shirt" },
  { id: "home", name: "Home & Furniture", icon: "Home" },
  { id: "books", name: "Books & Media", icon: "Book" },
  { id: "sports", name: "Sports & Outdoors", icon: "Mountain" },
  { id: "beauty", name: "Beauty & Personal Care", icon: "Sparkles" },
  { id: "food", name: "Food & Groceries", icon: "ShoppingCart" },
  { id: "auto", name: "Automotive", icon: "Car" },
  { id: "tools", name: "Tools & Hardware", icon: "Hammer" },
  { id: "games", name: "Games & Steam Keys", icon: "Gamepad2" },
  { id: "general", name: "General Retail", icon: "Store" },
  { id: "deals", name: "Deals & Coupons", icon: "Tag" }
];

export const getSitesByCategory = (categoryId: string) => {
  return cheapLifeSites.filter(site => site.category === categoryId);
};

export const getFeaturedSites = () => {
  return cheapLifeSites.filter(site => site.rating && site.rating >= 4.3);
};
